import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

interface QRScannerProps {
  onScan: (data: any) => void;
  onClose: () => void;
}

export const QRScanner = ({ onScan, onClose }: QRScannerProps) => {
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerDivId = 'qr-reader';

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (scannerRef.current) {
        scannerRef.current.stop().catch(logger.error);
      }
    };
  }, []);

  const startScanning = async () => {
    try {
      const html5QrCode = new Html5Qrcode(scannerDivId);
      scannerRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          try {
            const data = JSON.parse(decodedText);
            onScan(data);
            stopScanning();
            toast.success('Código QR escaneado exitosamente');
          } catch (error) {
            toast.error('Código QR inválido');
          }
        },
        (errorMessage) => {
          // Scanning error - can be ignored for most cases
        }
      );

      setScanning(true);
    } catch (error) {
      logger.error('Error starting scanner:', error);
      toast.error('Error al iniciar la cámara. Verifica los permisos.');
    }
  };

  const stopScanning = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        setScanning(false);
      } catch (error) {
        logger.error('Error stopping scanner:', error);
      }
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Escanear Código QR</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-4">
        <div
          id={scannerDivId}
          className="w-full rounded-lg overflow-hidden border-2 border-border"
          style={{ minHeight: scanning ? '300px' : '0' }}
        />

        {!scanning ? (
          <Button onClick={startScanning} className="w-full">
            <Camera className="w-4 h-4 mr-2" />
            Iniciar Escáner
          </Button>
        ) : (
          <Button onClick={stopScanning} variant="destructive" className="w-full">
            <X className="w-4 h-4 mr-2" />
            Detener Escáner
          </Button>
        )}

        <p className="text-xs text-muted-foreground text-center">
          Coloca el código QR frente a la cámara para escanearlo
        </p>
      </div>
    </Card>
  );
};
